import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { formatAmount } from "@/utils/formatAmount";
import apiClient from "@/api/client";
import { Badge } from "@/components/ui/Badge";
import { PageLoader } from "@/components/ui/Spinner";
import { MobileSortBar, SortableTh, TableSearchInput, useTableSort } from "@/components/ui/TableSort";
import { naturalCompare } from "@/utils/naturalCompare";

interface Invoice {
  id: string; invoice_number: string; order_number: string; amount_gross: string;
  status: "submitted" | "accepted" | "rejected" | "withdrawn";
  booking_status: "not_booked" | "booked";
  payment_status: "unpaid" | "partially_paid" | "transfer_prepared" | "paid";
  submitted_at: string;
}

const statusLabel = { submitted: "Oczekuje", accepted: "Przyjęta", rejected: "Nieprzyjęta", withdrawn: "Wycofana" };
const statusColor: Record<string, "gray" | "green" | "red" | "blue"> = { submitted: "blue", accepted: "green", rejected: "red", withdrawn: "gray" };
const paymentLabel: Record<string, string> = { unpaid: "Nieopłacona", partially_paid: "Opłacona częściowo", transfer_prepared: "Przelew przygotowany", paid: "Opłacona" };
const paymentColor: Record<string, "gray" | "green" | "yellow" | "blue"> = { unpaid: "gray", partially_paid: "yellow", transfer_prepared: "blue", paid: "green" };

type SortField = "invoice_number" | "order_number" | "amount_gross" | "status" | "payment_status" | "submitted_at";

const SORT_LABELS: Record<SortField, string> = {
  invoice_number: "Nr faktury",
  order_number: "Nr zamówienia",
  amount_gross: "Kwota brutto",
  status: "Status",
  payment_status: "Opłacenie",
  submitted_at: "Data",
};

// Kolejność cyklu życia, nie alfabetyczna — wycofane na końcu.
const STATUS_RANK: Record<Invoice["status"], number> = { submitted: 0, accepted: 1, rejected: 2, withdrawn: 3 };
const PAYMENT_RANK: Record<Invoice["payment_status"], number> = { unpaid: 0, partially_paid: 1, transfer_prepared: 2, paid: 3 };

function compareInvoices(a: Invoice, b: Invoice, field: SortField): number {
  switch (field) {
    case "invoice_number":
      return naturalCompare(a.invoice_number, b.invoice_number);
    case "order_number":
      return naturalCompare(a.order_number, b.order_number);
    case "amount_gross":
      return Number(a.amount_gross) - Number(b.amount_gross);
    case "status":
      return STATUS_RANK[a.status] - STATUS_RANK[b.status];
    case "payment_status":
      return PAYMENT_RANK[a.payment_status] - PAYMENT_RANK[b.payment_status];
    case "submitted_at":
      return new Date(a.submitted_at).getTime() - new Date(b.submitted_at).getTime();
  }
}

export function ContractorInvoicesPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["invoices"],
    queryFn: () => apiClient.get<{ data: Invoice[] }>("/invoices/").then((r) => r.data.data),
  });

  const { sortField, sortDir, handleSort, setField, toggleDir } = useTableSort<SortField>();
  const [search, setSearch] = useState("");

  const invoices = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q
      ? (data ?? []).filter(
          (inv) =>
            inv.invoice_number.toLowerCase().includes(q) ||
            inv.order_number.toLowerCase().includes(q),
        )
      : data ?? [];
    if (!sortField) return list;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...list].sort((a, b) => dir * compareInvoices(a, b, sortField));
  }, [data, search, sortField, sortDir]);

  return (
    <div>
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Moje faktury P-GK</h1>
        <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-6" />
      </div>
      {isLoading ? <PageLoader /> : (
        <>
          <TableSearchInput
            value={search}
            onChange={setSearch}
            placeholder="Szukaj: nr faktury / zamówienia…"
          />
          {/* Mobile sort bar — the table headers do this job on desktop */}
          <MobileSortBar
            id="contractor-invoices-sort"
            labels={SORT_LABELS}
            sortField={sortField}
            sortDir={sortDir}
            onFieldChange={setField}
            onDirToggle={toggleDir}
          />
          {/* Mobile card list */}
          <div className="sm:hidden flex flex-col divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800">
            {invoices.map((inv) => (
              <div key={inv.id} className={`px-4 py-3 flex items-start justify-between gap-3${inv.status === "withdrawn" ? " opacity-50" : ""}`}>
                <div className="min-w-0 flex-1">
                  <Link to={`/invoices/${inv.id}`} className={`font-medium text-[var(--color-primary)] hover:underline block${inv.status === "withdrawn" ? " line-through" : ""}`}>
                    {inv.invoice_number}
                  </Link>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{inv.order_number}</p>
                  <p className={`text-sm font-semibold text-gray-900 dark:text-gray-100 mt-1${inv.status === "withdrawn" ? " line-through" : ""}`}>{formatAmount(inv.amount_gross)} PLN</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{new Date(inv.submitted_at).toLocaleDateString("pl")}</p>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <Badge color={statusColor[inv.status]}>{statusLabel[inv.status]}</Badge>
                  <Badge color={paymentColor[inv.payment_status]}>{paymentLabel[inv.payment_status]}</Badge>
                </div>
              </div>
            ))}
            {invoices.length === 0 && <p className="px-4 py-8 text-center text-gray-400 dark:text-gray-500 text-sm">{search.trim() ? "Brak faktur pasujących do wyszukiwania" : "Brak faktur"}</p>}
          </div>
          {/* Desktop table */}
          <div className="hidden sm:block bg-white dark:bg-gray-800 overflow-x-auto">
            <table className="w-full text-xs min-w-[500px]">
              <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <SortableTh field="invoice_number" label="Nr faktury" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortableTh field="order_number" label="Nr zamówienia" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortableTh field="amount_gross" label="Kwota brutto" align="right" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortableTh field="status" label="Status" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortableTh field="payment_status" label="Opłacenie" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortableTh field="submitted_at" label="Data" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {invoices.map((inv) => (
                  <tr key={inv.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 border-l-2 border-transparent hover:border-[var(--color-primary)] transition-colors cursor-pointer${inv.status === "withdrawn" ? " opacity-50" : ""}`}>
                    <td className="px-4 py-3 font-medium text-[var(--color-primary)] hover:underline">
                      <Link to={`/invoices/${inv.id}`} className={inv.status === "withdrawn" ? "line-through" : ""}>{inv.invoice_number}</Link>
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{inv.order_number}</td>
                    <td className={`px-4 py-3 text-right font-medium${inv.status === "withdrawn" ? " line-through" : ""}`}>{formatAmount(inv.amount_gross)} PLN</td>
                    <td className="px-4 py-3"><Badge color={statusColor[inv.status]}>{statusLabel[inv.status]}</Badge></td>
                    <td className="px-4 py-3"><Badge color={paymentColor[inv.payment_status]}>{paymentLabel[inv.payment_status]}</Badge></td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{new Date(inv.submitted_at).toLocaleDateString("pl")}</td>
                  </tr>
                ))}
                {invoices.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400 dark:text-gray-500">{search.trim() ? "Brak faktur pasujących do wyszukiwania" : "Brak faktur"}</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
